import useGet from './api/useGet'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import usePost from './api/usePost'
import { ApiRequestStatus } from '../models/api/api-request-status'
import type { Member } from '../models/member/member'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../events/event-key'
import { useCoverObjective } from './use-cover-objective'

export type ObjectiveSection = 'proposals' | 'running' | 'agreed'

export const useObjectives = (
  params: () => { feedbackTopicId?: string; ownerId: string; dialogId: string },
  options?: { autoCoverCreates?: boolean },
) => {
  const { data, load, status, reload } = useGet<ObjectivesByFeedbackTopicViewModel>(`objectives`)
  const objectiveUpdatedBus = useEventBus(EventKey.objectiveUpdated)
  const { cover } = useCoverObjective(() => params().dialogId)

  const query = computed(() => {
    const { feedbackTopicId, dialogId, ownerId } = params()

    return {
      ...(feedbackTopicId && { feedbackTopicId }),
      dialogId,
      ownerId,
    }
  })

  const reloadList = () => {
    reload({ query: query.value })
  }

  onMounted(() => {
    if (query.value.dialogId && query.value.ownerId) {
      load({ query: query.value })
    }
  })

  watch(query, () => {
    if (query.value.dialogId && query.value.ownerId) {
      load({ query: query.value })
    }
  })

  objectiveUpdatedBus.on(() => reloadList())

  const { mutate: create, status: createStatus, data: createdId } = usePost<
    string,
    CreateableObjective
  >(`objectives`)

  const creatableObjective = reactive<Partial<CreateableObjective>>({
    createDialogId: params().dialogId,
    ownerId: params().ownerId,
    isImpulse: false,
  })

  const creatableImpulse = reactive<Partial<CreateableImpulse>>({
    createDialogId: params().dialogId,
    ownerId: params().ownerId,
    isImpulse: true,
  })

  const saveObjective = async () => {
    if (creatableObjective && creatableObjective.title) {
      await create({
        ...creatableObjective,
        ...query.value,
        title: creatableObjective.title,
        isImpulse: false,
      })
      if (createStatus.value == ApiRequestStatus.Success) {
        const id = typeof createdId.value === 'string' ? createdId.value : undefined
        if (options?.autoCoverCreates && id) {
          await cover(id)
        }
        cancelCreateObjective()
      }
      objectiveUpdatedBus.emit()
    }
  }

  const saveImpulse = async () => {
    if (creatableImpulse && creatableImpulse.title) {
      await create({
        ...creatableImpulse,
        ...query.value,
        title: creatableImpulse.title,
        isImpulse: true,
      })
      if (createStatus.value == ApiRequestStatus.Success) {
        cancelCreateImpulse()
      }
      objectiveUpdatedBus.emit()
    }
  }

  const showForm = ref(false)
  const showCreateDetails = ref(false)

  const createObjective = () => {
    showForm.value = true
  }

  const cancelCreateObjective = () => {
    showForm.value = false
    showCreateDetails.value = false
    creatableObjective.title = undefined
    creatableObjective.description = undefined
    creatableObjective.feedbackTopicId = undefined
  }

  const cancelCreateImpulse = () => {
    showForm.value = false
    creatableImpulse.title = undefined
  }

  const allObjectives = computed(() => [
    ...(data.value?.createdObjectives ?? []),
    ...(data.value?.ongoingObjectives ?? []),
  ])

  const classifySection = (objective: Objective): ObjectiveSection => {
    if (objective.isImpulse) return 'proposals'
    const isOngoing = data.value?.ongoingObjectives.some((o) => o.id === objective.id)
    if (isOngoing) return 'running'
    return 'agreed'
  }

  /** Sticky section membership — rows stay put mid-dialog, except a promoted Vorschlag. */
  const stickySectionById = ref<Map<string, ObjectiveSection>>(new Map())
  const impulseById = ref<Map<string, boolean>>(new Map())

  const rememberImpulses = () => {
    const next = new Map<string, boolean>()
    for (const objective of allObjectives.value) {
      next.set(objective.id, objective.isImpulse)
    }
    impulseById.value = next
  }

  const rebucketSections = () => {
    const next = new Map<string, ObjectiveSection>()
    for (const objective of allObjectives.value) {
      next.set(objective.id, classifySection(objective))
    }
    stickySectionById.value = next
    rememberImpulses()
  }

  const syncStickySections = () => {
    if (stickySectionById.value.size === 0) {
      rebucketSections()
      return
    }

    const next = new Map(stickySectionById.value)
    for (const objective of allObjectives.value) {
      const wasImpulse = impulseById.value.get(objective.id)
      const promoted = wasImpulse === true && !objective.isImpulse
      if (!next.has(objective.id) || promoted) {
        next.set(objective.id, classifySection(objective))
      }
    }
    // Drop ids that disappeared (deleted / discarded)
    for (const id of next.keys()) {
      if (!allObjectives.value.some((o) => o.id === id)) {
        next.delete(id)
      }
    }
    stickySectionById.value = next
    rememberImpulses()
  }

  watch(
    allObjectives,
    () => {
      syncStickySections()
    },
    { deep: true },
  )

  const sectionOf = (objectiveId: string): ObjectiveSection | undefined =>
    stickySectionById.value.get(objectiveId)

  const proposals = computed(() =>
    allObjectives.value
      .filter((o) => sectionOf(o.id) === 'proposals')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  )

  const runningObjectives = computed(() =>
    allObjectives.value
      .filter((o) => sectionOf(o.id) === 'running')
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
  )

  const agreedObjectives = computed(() =>
    allObjectives.value
      .filter((o) => sectionOf(o.id) === 'agreed')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  )

  /**
   * Everything on the objectives slide counts toward “besprochen”:
   * proposals, pre-existing (ongoing), and goals created/agreed in this dialog.
   * A promoted Vorschlag stays a single entry.
   */
  const reviewObjectiveIds = computed(() => {
    const ids = new Set<string>()

    for (const objective of allObjectives.value) {
      ids.add(objective.id)
    }

    // Sticky proposal ids stay in the set even if a reload briefly drops them.
    for (const [id, section] of stickySectionById.value.entries()) {
      if (section === 'proposals') {
        ids.add(id)
      }
    }

    return ids
  })

  const reviewDenominator = computed(() => reviewObjectiveIds.value.size)

  const coveredReviewCount = computed(() => {
    const ids = reviewObjectiveIds.value
    return allObjectives.value.filter((o) => ids.has(o.id) && !!o.isCovered).length
  })

  const uncoveredReviewCount = computed(() =>
    Math.max(0, reviewDenominator.value - coveredReviewCount.value),
  )

  const totalNumberOfObjectives = computed(() => allObjectives.value.length)

  return {
    status,
    data,
    totalNumberOfObjectives,
    saveObjective,
    createObjective,
    cancelCreateObjective,
    creatableObjective,
    creatableImpulse,
    showForm,
    showCreateDetails,
    saveImpulse,
    proposals,
    runningObjectives,
    agreedObjectives,
    reviewDenominator,
    coveredReviewCount,
    uncoveredReviewCount,
    rebucketSections,
    reloadList,
  }
}

export interface ObjectiveAction {
  readonly id: string
  readonly title: string
  readonly isChecked: boolean
  readonly position: number
}

export interface CreateableObjective {
  title: string
  description?: string
  feedbackTopicId?: string
  createDialogId?: string
  ownerId?: string
  isImpulse: boolean
}

export interface CreateableImpulse {
  title: string
  feedbackTopicId: string
  createDialogId: string
  ownerId: string
  isImpulse: boolean
}

export interface Objective {
  readonly id: string
  readonly title: string
  readonly description: string | null
  readonly owner: Member
  readonly author: Member
  readonly createDialogId?: string
  readonly createdAt: string
  readonly isImpulse: boolean
  readonly feedbackTopicId: string
  readonly achievedAt: string | null
  readonly archivedAt: string | null
  readonly actions: ReadonlyArray<ObjectiveAction>
  readonly canDelete: boolean
  /** Coach ticked this off the dialog checklist (“besprochen”). Absent outside dialog context. */
  readonly isCovered?: boolean
}

export interface ObjectivesByFeedbackTopicViewModel {
  readonly createdObjectives: ReadonlyArray<Objective>
  readonly ongoingObjectives: ReadonlyArray<Objective>
}
